const exampleSiteGroup = {
  availableDatasources: [],
  companyDisplayName: 'Example Company',
  companyId: 'COMPANY_ID',
  createTime: '2026-01-15T12:00:00Z',
  description: 'Screens in the downtown area',
  devicesCount: 3,
  devicesCountLimit: 10,
  displayName: 'Downtown screens',
  enabledCmsIntegrations: [],
  enabledIntegrations: [],
  enabledSolutions: [],
  heatmapEnabled: false,
  id: 'PROJECT_ID',
  isSiteGroup: true,
  labels: {},
  name: 'projects/PROJECT_ID',
  ownerEmail: 'owner@example.com',
  ownerId: 'USER_ID',
  preferences: {
    enableAnomalyDetection: false,
    maxConfidenceAge: 0,
    maxConfidenceEmotion: 0,
    maxConfidenceGender: 0,
    maxDwellTime: 0,
    maxSessionTime: 0,
    minConfidenceAge: 0,
    minConfidenceEmotion: 0,
    minConfidenceGender: 0,
    speedUnit: 'SPEED_UNIT_NOT_DEFINED',
    timezone: 'America/New_York',
    vehicleMultiplier: {},
    viewMetric: 'ATTENTION',
    viewThreshold: 0
  },
  provisioningToken: 'REDACTED',
  sitesCount: 2,
  tags: [],
  updateTime: '2026-01-16T12:00:00Z'
};

const exampleSite = {
  id: 'SITE_ID',
  name: 'companies/COMPANY_ID/projects/PROJECT_ID/sites/SITE_ID',
  displayName: 'Main entrance',
  type: { id: 'SITE_TYPE_ID', displayName: 'Retail' },
  status: 'ENABLED',
  description: 'Street-facing entrance',
  solution: 'AUDIENCE',
  projectId: 'PROJECT_ID',
  address: '123 Main Street',
  latitude: 40.7128,
  longitude: -74.006,
  geometry: { type: 'Point', coordinates: [-74.006, 40.7128] },
  attachedDevices: ['DEVICE_ID'],
  devicesCount: 1,
  countryCode: 'US',
  companyId: 'COMPANY_ID',
  createTime: '2026-01-15T12:00:00Z',
  updateTime: '2026-01-16T12:00:00Z'
};

const exampleDevice = {
  id: 'DEVICE_ID',
  name: 'companies/COMPANY_ID/devices/DEVICE_ID',
  displayName: 'Entrance sensor',
  creatorId: 'USER_ID',
  projectId: 'PROJECT_ID',
  companyId: 'COMPANY_ID',
  provisioningToken: 'REDACTED',
  provisioningTokenWasUsed: true,
  createTime: '2026-01-15T12:00:00Z',
  updateTime: '2026-01-16T12:00:00Z',
  labels: { floor: 'ground' },
  tags: ['entrance'],
  archived: false,
  solution: 'FACEV2',
  state: {
    status: 'detecting',
    updateTime: '2026-01-16T12:00:00Z',
    online: true
  },
  hasWifi: false,
  attachedSites: ['SITE_ID'],
  sites: [{ id: 'SITE_ID', displayName: 'Main entrance', status: 'ENABLED' }]
};

export const developerArticles = [
  {
    slug: 'public-api-getting-started',
    title: 'Getting started',
    summary: 'Start with an API key. Existing integrations can also use an access token.',
    category: 'developer',
    sources: [],
    sections: [
      {
        id: 'request-conventions',
        title: 'Making API requests',
        paragraphs: [
          'You can use the Developer API to manage Site Groups, sites, and devices, or retrieve detection data through jobs. Each endpoint has its own path under the base URL below. The API returns JSON and accepts JSON bodies for POST and PATCH requests.',
          'Send your API key as a bearer token in the `Authorization` header. If your integration already uses an access token, you can keep sending it in the same header. Access tokens are a legacy authentication option.'
        ],
        tables: [
          {
            caption: 'Base URL and request format',
            headers: ['Item', 'Value'],
            rows: [
              ['Base URL', '`https://api.admobilize.com/v1alpha1`'],
              ['API key header', '`Authorization: Bearer adm_pk_<key-id>_<secret>`'],
              ['Access token (legacy)', '`Authorization: Bearer ACCESS_TOKEN`'],
              ['Request body', '`application/json` when a body is required'],
              ['Response body', 'JSON']
            ]
          }
        ]
      },
      {
        id: 'api-keys',
        title: 'API keys',
        paragraphs: [
          'In Dashboard Profile, you can create a key for each server integration. Managing API keys shows where to find the controls and how to replace a key later. Each key has the same access to Site Groups, sites, devices, and data as the user who created it.',
          'Treat the full key as a secret. The Dashboard shows it only when you create it; afterward, you can see its prefix and metadata, but not the secret. Save it in a secret manager and keep it out of client-side code, URLs, logs, and source control.',
          'You can set an expiry date or leave a key valid until you revoke it. Expired and revoked keys return HTTP 401.',
          'API keys authenticate public API requests only. Use a Dashboard sign-in token for Dashboard and key management requests. To try the API, set `ADMOBILIZE_API_KEY` to the full secret and run this request.'
        ],
        paragraphLinks: [{ label: 'Managing API keys', slug: 'managing-api-keys', section: 'create-api-key' }],
        code: 'curl -H "Authorization: Bearer $ADMOBILIZE_API_KEY" \\\n  "https://api.admobilize.com/v1alpha1/projects?pageSize=10"'
      },
      {
        id: 'jwt-login',
        title: 'Access tokens for existing integrations',
        paragraphs: [
          'If your integration already signs in with an email address and password, it can keep using that flow. Send the credentials to the endpoint below, then use the returned `accessToken` as a bearer token on public API requests. Keep credentials and tokens out of URLs, logs, and source control.',
          'The response also includes `refreshToken` and `expiresIn`. Store the refresh token securely so you can renew access. Read `expiresIn` after every sign-in and refresh; token lifetimes can change.'
        ],
        tables: [
          {
            caption: 'Sign-in request and response',
            headers: ['Item', 'Value'],
            rows: [
              ['Method and URL', '`POST https://xauth.admobilize.com/api/v1/users:login`'],
              ['Content-Type', '`application/json`'],
              ['Request fields', '`email`, `password`'],
              ['Response fields', '`accessToken`, `refreshToken`, `expiresIn`']
            ]
          }
        ],
        code: 'curl -X POST -H "Content-Type: application/json" \\\n  -d \'{"email":"YOUR_EMAIL","password":"YOUR_PASSWORD"}\' \\\n  "https://xauth.admobilize.com/api/v1/users:login"'
      },
      {
        id: 'jwt-refresh',
        title: 'Refresh an access token',
        paragraphs: [
          'Before your access token expires, send the current `refreshToken` to the endpoint below. The response contains a new `accessToken`, `refreshToken`, and `expiresIn`. Save both new tokens after a successful refresh.',
          'If the refresh token has expired or is invalid, sign in again. Fix invalid credentials before retrying a failed sign-in. If login or refresh is rate limited, follow the guidance in the response.'
        ],
        tables: [
          {
            caption: 'Refresh request',
            headers: ['Item', 'Value'],
            rows: [
              ['Method and URL', '`POST https://xauth.admobilize.com/api/v1/users:refreshToken`'],
              ['Content-Type', '`application/json`'],
              ['Request field', '`refreshToken`']
            ]
          }
        ],
        code: 'curl -X POST -H "Content-Type: application/json" \\\n  -d \'{"refreshToken":"YOUR_REFRESH_TOKEN"}\' \\\n  "https://xauth.admobilize.com/api/v1/users:refreshToken"'
      },
      {
        id: 'rate-limits',
        title: 'Rate limits and retries',
        paragraphs: [
          'The public API allows 5 requests per second per user, measured over a one-minute window. Exceeding the limit returns HTTP 429 and blocks requests for two minutes. All API keys and access tokens owned by that user share the limit.',
          'Page through lists, cache responses you can reuse, and space out status checks. After a 429, wait at least two minutes before retrying. A blocked request returns a response like this. The `requestId` varies by request.'
        ],
        code: 'HTTP/1.1 429 Too Many Requests\nContent-Type: application/problem+json; charset=UTF-8\n\n{\n  "type": "https://api.admobilize.com/problems/v1/rate-limited",\n  "title": "Too many requests",\n  "status": 429,\n  "code": "RATE_LIMITED",\n  "detail": "Try again later.",\n  "params": {},\n  "requestId": "req_example123",\n  "retryable": true\n}'
      },
      {
        id: 'pagination',
        title: 'Read every page',
        paragraphs: [
          'Site Group, site, and device lists and job results use `pageSize` and `pageToken`. Leave out `pageToken` for the first page. If you get a nonempty `nextPageToken`, use it as `pageToken` in the next request. An empty token means you have reached the last page.',
          'Keep the same filters and sort order on every page, and pass the token back exactly as you received it.'
        ],
        tables: [
          {
            caption: 'Pagination parameters',
            headers: ['Name', 'Location', 'Purpose'],
            rows: [
              ['`pageSize`', 'Request query', 'Number of records requested per page'],
              ['`pageToken`', 'Request query', '`nextPageToken` from the preceding page'],
              ['`nextPageToken`', 'Response body', 'Token to continue; empty after the last page'],
              ['`totalSize`', 'List response body', 'Total matching records when supplied']
            ]
          }
        ],
        code: 'curl -H "Authorization: Bearer $ADMOBILIZE_API_KEY" \\\n  "https://api.admobilize.com/v1alpha1/projects?pageSize=10&pageToken=NEXT_PAGE_TOKEN"'
      },
      {
        id: 'patch-requests',
        title: 'Update resources',
        paragraphs: [
          'To update a Site Group, site, or device, send a JSON body to its PATCH route and list the fields in `updateMask`. Separate multiple field names with commas. The response contains the updated resource.',
          'Read the resource first. Its article lists the fields you can change and explains when your values replace the existing ones.'
        ],
        tables: [
          {
            caption: 'Shared PATCH format',
            headers: ['Item', 'Value'],
            rows: [
              ['Content-Type', '`application/json`'],
              ['Query parameter', '`updateMask=displayName`'],
              ['Body', '{"displayName":"New name"}']
            ]
          }
        ]
      },
      {
        id: 'errors',
        title: 'Handle errors',
        paragraphs: [
          'Check the HTTP status before using a response. An API error may include `code`, `detail`, and `requestId`. If you contact support, send them the `requestId` from the failed request.',
          'Authentication errors may have a different response body from gateway errors. Check which fields are present before reading them.'
        ],
        tables: [
          {
            caption: 'Common HTTP responses',
            headers: ['Status', 'What to check'],
            rows: [
              ['400', 'Request fields, query parameters, and `updateMask`'],
              ['401', 'Missing, invalid, expired, or revoked credential'],
              ['403', 'Permission for the requested action'],
              ['404', 'Resource ID or access to the requested resource'],
              ['429', 'Per-user rate limit; wait at least two minutes'],
              ['503', 'Authentication service availability']
            ]
          }
        ]
      }
    ],
    dashboardLinks: [{ label: 'Manage API keys in Profile', to: '/profile' }],
    related: ['managing-api-keys', 'public-api-projects', 'public-api-sites-and-devices', 'public-api-data-jobs'],
    legacy: false
  },
  {
    slug: 'public-api-projects',
    title: 'Site Groups (Projects)',
    summary: 'Find your Site Groups, check their statistics, and update their settings.',
    category: 'developer',
    sources: [],
    sections: [
      {
        id: 'routes',
        title: 'Site Group routes',
        paragraphs: [
          'Site Group is the new name for what the Dashboard used to call a Project. The API still uses `projects` in its paths. Use a Site Group ID for `{pid}` below and in site and device routes. `GET /projects` lists the Site Groups you can access, excluding deleted ones.',
          'The examples use placeholder IDs and values. The Site Group resource samples include the fields in the published API schema; the provisioning token value is redacted.'
        ],
        tables: [
          {
            caption: 'Site Group routes under https://api.admobilize.com/v1alpha1',
            headers: ['Method', 'Path', 'Purpose'],
            rows: [
              ['GET', '`/projects`', 'List accessible Site Groups'],
              ['GET', '`/projects/{pid}`', 'Get one Site Group’s configuration and metadata'],
              ['GET', '`/projects/{pid}/stats`', 'Get device counts by status'],
              ['PATCH', '`/projects/{pid}?updateMask=...`', 'Update selected Site Group fields']
            ]
          }
        ]
      },
      {
        id: 'list-response',
        title: 'List Site Groups',
        paragraphs: [
          'The `projects` array contains each Site Group’s `id` and `displayName`. Use `totalSize` to see how many match, and follow the shared pagination rules if `nextPageToken` points to another page.'
        ],
        paragraphLinks: [
          { label: 'shared pagination rules', slug: 'public-api-getting-started', section: 'pagination' }
        ],
        tables: [
          {
            caption: 'Site Group list query parameters',
            headers: ['Parameter', 'Required', 'Purpose'],
            rows: [
              ['`pageSize`', 'No', 'Number of Site Groups requested per page; defaults to 10'],
              ['`pageToken`', 'No', '`nextPageToken` from the preceding page'],
              ['`filter`', 'No', 'Filter expression for the Site Group list'],
              ['`orderBy`', 'No', 'Sort expression for the Site Group list']
            ]
          }
        ],
        code: 'curl -H "Authorization: Bearer $ADMOBILIZE_API_KEY" \\\n  "https://api.admobilize.com/v1alpha1/projects?pageSize=10"',
        response: JSON.stringify(
          {
            projects: [exampleSiteGroup],
            nextPageToken: '',
            totalSize: 1
          },
          null,
          2
        )
      },
      {
        id: 'read-and-stats',
        title: 'Get a Site Group',
        paragraphs: [
          'Use `GET /projects/{pid}` to read a Site Group’s name, description, and configuration. Replace `PROJECT_ID` with an `id` from the list response. You need access to the Site Group.'
        ],
        code: 'curl -H "Authorization: Bearer $ADMOBILIZE_API_KEY" \\\n  "https://api.admobilize.com/v1alpha1/projects/PROJECT_ID"',
        response: JSON.stringify(exampleSiteGroup, null, 2)
      },
      {
        id: 'stats',
        title: 'Get Site Group statistics',
        paragraphs: [
          'Use `GET /projects/{pid}/stats` for device counts by status. Use the same Site Group ID as the details request.',
          '`detecting`, `downloadingModels`, `inactive`, and `noCamera` count online devices in those states, so they are already included in `online`.'
        ],
        code: 'curl -H "Authorization: Bearer $ADMOBILIZE_API_KEY" \\\n  "https://api.admobilize.com/v1alpha1/projects/PROJECT_ID/stats"',
        response: JSON.stringify(
          {
            deviceCountByStatus: {
              total: 3,
              online: 2,
              offline: 1,
              detecting: 2,
              downloadingModels: 0,
              inactive: 0,
              noCamera: 0
            }
          },
          null,
          2
        )
      },
      {
        id: 'update',
        title: 'Update a Site Group',
        paragraphs: [
          'Use the shared PATCH format to change the fields below. If you update `preferences`, send the whole object with `updateMask=preferences`. Settings you leave out can reset to their defaults.'
        ],
        paragraphLinks: [
          { label: 'shared PATCH format', slug: 'public-api-getting-started', section: 'patch-requests' }
        ],
        tables: [
          {
            caption: 'Site Group update fields',
            headers: ['Fields', 'Behavior'],
            rows: [
              ['`displayName`', 'Change the Site Group name'],
              ['`description`', 'Change the Site Group description'],
              ['`tags`', 'Replace the tags'],
              ['`labels`', 'Replace the labels']
            ]
          }
        ],
        code: 'curl -X PATCH \\\n  -H "Authorization: Bearer $ADMOBILIZE_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"displayName":"Downtown displays"}\' \\\n  "https://api.admobilize.com/v1alpha1/projects/PROJECT_ID?updateMask=displayName"',
        response: JSON.stringify(
          {
            ...exampleSiteGroup,
            displayName: 'Downtown displays',
            updateTime: '2026-01-17T12:00:00Z'
          },
          null,
          2
        )
      }
    ],
    dashboardLinks: [{ label: 'Open Sites', to: '/sites' }],
    related: ['public-api-getting-started', 'public-api-sites-and-devices', 'public-api-data-jobs'],
    legacy: false
  },
  {
    slug: 'public-api-sites-and-devices',
    title: 'Sites and devices',
    summary: 'Find the sites and devices in a Site Group, update their settings, and check device status.',
    category: 'developer',
    sources: [],
    sections: [
      {
        id: 'scope',
        title: 'Site Group scope',
        paragraphs: [
          'Site and device paths start with `/projects/{pid}`. Replace `{pid}` with a Site Group ID, and use the site or device ID for `{sid}` or `{did}`. You need access to the Site Group and permission for the action you request.'
        ]
      },
      {
        id: 'sites',
        title: 'Site routes',
        paragraphs: [
          'A site is a location within a Site Group. List sites to find an ID, then fetch that site to see its current configuration. Before sending `DELETE`, check the site ID and its attached resources.'
        ],
        tables: [
          {
            caption: 'Site routes under https://api.admobilize.com/v1alpha1',
            headers: ['Method', 'Path', 'Purpose'],
            rows: [
              ['GET', '`/projects/{pid}/sites`', 'List sites in the Site Group'],
              ['GET', '`/projects/{pid}/sites/{sid}`', 'Get one site'],
              ['PATCH', '`/projects/{pid}/sites/{sid}?updateMask=...`', 'Update selected site fields'],
              ['DELETE', '`/projects/{pid}/sites/{sid}`', 'Delete the site']
            ]
          }
        ]
      },
      {
        id: 'list-sites',
        title: 'List sites',
        paragraphs: [
          'Use `GET /projects/{pid}/sites` to find site IDs in a Site Group. The response includes `sites`, `totalSize`, and a `nextPageToken` when another page is available. These examples show the fields relevant to each task; a site can include other fields.'
        ],
        code: 'curl -H "Authorization: Bearer $ADMOBILIZE_API_KEY" \\\n  "https://api.admobilize.com/v1alpha1/projects/PROJECT_ID/sites?pageSize=10"',
        response: JSON.stringify(
          {
            sites: [exampleSite],
            nextPageToken: '',
            totalSize: 1
          },
          null,
          2
        )
      },
      {
        id: 'get-site',
        title: 'Get a site',
        paragraphs: [
          'Use a site ID from the list response to read its current details before you update or delete it.'
        ],
        code: 'curl -H "Authorization: Bearer $ADMOBILIZE_API_KEY" \\\n  "https://api.admobilize.com/v1alpha1/projects/PROJECT_ID/sites/SITE_ID"',
        response: JSON.stringify(exampleSite, null, 2)
      },
      {
        id: 'site-updates',
        title: 'Update a site',
        paragraphs: [
          'Use the shared PATCH format to change a site’s `displayName` or `description`. Fetch the site again to check the saved values.'
        ],
        paragraphLinks: [
          { label: 'shared PATCH format', slug: 'public-api-getting-started', section: 'patch-requests' }
        ],
        tables: [
          {
            caption: 'Site update fields',
            headers: ['Field', 'Use'],
            rows: [
              ['`displayName`', 'Change the site name'],
              ['`description`', 'Change the site description']
            ]
          }
        ],
        code: 'curl -X PATCH \\\n  -H "Authorization: Bearer $ADMOBILIZE_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"displayName":"North entrance"}\' \\\n  "https://api.admobilize.com/v1alpha1/projects/PROJECT_ID/sites/SITE_ID?updateMask=displayName"',
        response: JSON.stringify(
          {
            ...exampleSite,
            displayName: 'North entrance',
            updateTime: '2026-01-17T12:00:00Z'
          },
          null,
          2
        )
      },
      {
        id: 'delete-site',
        title: 'Delete a site',
        paragraphs: [
          'Send `DELETE` when you no longer need the site. Check the Site Group and site IDs first. A successful request returns an empty JSON object.'
        ],
        code: 'curl -X DELETE -H "Authorization: Bearer $ADMOBILIZE_API_KEY" \\\n  "https://api.admobilize.com/v1alpha1/projects/PROJECT_ID/sites/SITE_ID"',
        response: '{}'
      },
      {
        id: 'devices',
        title: 'Device routes',
        paragraphs: [
          'List devices to find an ID and check the current settings. Use `:archive` or `:dearchive` to archive or restore a device; both actions return the device. `DELETE` removes it.'
        ],
        tables: [
          {
            caption: 'Device routes under https://api.admobilize.com/v1alpha1',
            headers: ['Method', 'Path', 'Purpose'],
            rows: [
              ['GET', '`/projects/{pid}/devices`', 'List devices in the Site Group'],
              ['GET', '`/projects/{pid}/devices/{did}`', 'Get one device'],
              ['PATCH', '`/projects/{pid}/devices/{did}?updateMask=...`', 'Update selected device fields'],
              ['DELETE', '`/projects/{pid}/devices/{did}`', 'Delete the device'],
              ['POST', '`/projects/{pid}/devices/{did}:archive`', 'Archive the device'],
              ['POST', '`/projects/{pid}/devices/{did}:dearchive`', 'Restore the archived device']
            ]
          }
        ]
      },
      {
        id: 'list-devices',
        title: 'List devices',
        paragraphs: [
          'Use `GET /projects/{pid}/devices` to find device IDs and check their current settings. Follow `nextPageToken` when the list has another page. These examples show the fields relevant to each task; a device can include other fields. The provisioning token in the samples is redacted. The API can return `FACEV2` as the identifier for an Audience Device.'
        ],
        code: 'curl -H "Authorization: Bearer $ADMOBILIZE_API_KEY" \\\n  "https://api.admobilize.com/v1alpha1/projects/PROJECT_ID/devices?pageSize=10"',
        response: JSON.stringify(
          {
            devices: [exampleDevice],
            nextPageToken: '',
            totalSize: 1
          },
          null,
          2
        )
      },
      {
        id: 'get-device',
        title: 'Get a device',
        paragraphs: ['Use a device ID from the list response to read its configuration and latest reported state.'],
        code: 'curl -H "Authorization: Bearer $ADMOBILIZE_API_KEY" \\\n  "https://api.admobilize.com/v1alpha1/projects/PROJECT_ID/devices/DEVICE_ID"',
        response: JSON.stringify(exampleDevice, null, 2)
      },
      {
        id: 'device-status',
        title: 'Read device status',
        paragraphs: [
          'In a device response, `state.status` shows the device state, and `state.updateTime` shows when the service last received it. In a list response, find these fields under `devices[].state`. Changes can take several minutes to appear. Keep `updateTime` with each status you record so you can tell a new report from an old one.',
          'Check the device collection no more than once every 15 minutes. To alert people, use Device notifications. If `state` is missing or stale, treat the status as unknown until you check the device and its network.'
        ],
        paragraphLinks: [{ label: 'Device notifications', slug: 'device-notifications', section: 'configure' }],
        tables: [
          {
            caption: 'Device states',
            headers: ['Status', 'Severity', 'Meaning'],
            rows: [
              ['`detecting`', 'OK', 'Connected and sending detections'],
              ['`downloading-models`', 'OK', 'Connected and preparing models'],
              ['`inactive`', 'Warning', 'Connected but not sending detections'],
              ['`no-camera`', 'Warning', 'Connected without a usable camera input'],
              ['`offline`', 'Offline', 'No connection since `state.updateTime`']
            ]
          }
        ],
        code: 'curl -H "Authorization: Bearer $ADMOBILIZE_API_KEY" \\\n  "https://api.admobilize.com/v1alpha1/projects/PROJECT_ID/devices/DEVICE_ID"',
        response: JSON.stringify(exampleDevice, null, 2)
      },
      {
        id: 'device-updates',
        title: 'Update a device',
        paragraphs: [
          'Fetch the device to check its current settings, then use the shared PATCH format to change its `displayName`, `tags`, or `labels`.'
        ],
        paragraphLinks: [
          { label: 'shared PATCH format', slug: 'public-api-getting-started', section: 'patch-requests' }
        ],
        tables: [
          {
            caption: 'Device update fields',
            headers: ['Field', 'Use'],
            rows: [
              ['`displayName`', 'Change the device name'],
              ['`tags`', 'Replace the tags'],
              ['`labels`', 'Replace the labels']
            ]
          }
        ],
        code: 'curl -X PATCH \\\n  -H "Authorization: Bearer $ADMOBILIZE_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"displayName":"Lobby sensor"}\' \\\n  "https://api.admobilize.com/v1alpha1/projects/PROJECT_ID/devices/DEVICE_ID?updateMask=displayName"',
        response: JSON.stringify(
          {
            ...exampleDevice,
            displayName: 'Lobby sensor',
            updateTime: '2026-01-17T12:00:00Z'
          },
          null,
          2
        )
      },
      {
        id: 'delete-device',
        title: 'Delete a device',
        paragraphs: [
          'Send `DELETE` when the device is no longer needed. Check the Site Group and device IDs first. A successful request returns an empty JSON object.'
        ],
        code: 'curl -X DELETE -H "Authorization: Bearer $ADMOBILIZE_API_KEY" \\\n  "https://api.admobilize.com/v1alpha1/projects/PROJECT_ID/devices/DEVICE_ID"',
        response: '{}'
      },
      {
        id: 'archive-device',
        title: 'Archive a device',
        paragraphs: [
          'Send `POST` to `:archive` to archive a device. The response is the device with `archived` set to `true` and an `archiveTime`.'
        ],
        code: 'curl -X POST -H "Authorization: Bearer $ADMOBILIZE_API_KEY" \\\n  "https://api.admobilize.com/v1alpha1/projects/PROJECT_ID/devices/DEVICE_ID:archive"',
        response: JSON.stringify(
          {
            ...exampleDevice,
            archived: true,
            updateTime: '2026-01-18T12:00:00Z',
            archiveTime: '2026-01-18T12:00:00Z'
          },
          null,
          2
        )
      },
      {
        id: 'restore-device',
        title: 'Restore an archived device',
        paragraphs: [
          'Send `POST` to `:dearchive` to restore an archived device. The response is the device with `archived` set to `false`.'
        ],
        code: 'curl -X POST -H "Authorization: Bearer $ADMOBILIZE_API_KEY" \\\n  "https://api.admobilize.com/v1alpha1/projects/PROJECT_ID/devices/DEVICE_ID:dearchive"',
        response: JSON.stringify(
          {
            ...exampleDevice,
            updateTime: '2026-01-19T12:00:00Z'
          },
          null,
          2
        )
      }
    ],
    dashboardLinks: [
      { label: 'Open Devices', to: '/devices' },
      { label: 'Open Sites', to: '/sites' }
    ],
    related: ['public-api-getting-started', 'public-api-projects', 'device-notifications'],
    legacy: false
  },
  {
    slug: 'public-api-data-jobs',
    title: 'Data jobs',
    summary: 'Create a job to retrieve detection records or export them to a file.',
    category: 'developer',
    sources: [],
    sections: [
      {
        id: 'routes',
        title: 'Job routes',
        paragraphs: [
          'Data jobs prepare bulk detection data in the background. Save the `jobId` when you create one; you will need it to check progress and retrieve the output.'
        ],
        tables: [
          {
            caption: 'Data job routes under https://api.admobilize.com/v1alpha1',
            headers: ['Method', 'Path', 'Purpose'],
            rows: [
              ['POST', '`/jobs`', 'Create a data job'],
              ['GET', '`/jobs/{jid}`', 'Get job status'],
              ['GET', '`/jobs/{jid}/results`', 'Read a page of results'],
              ['GET', '`/jobs/{jid}/export?format=csv`', 'Get file download URLs']
            ]
          }
        ]
      },
      {
        id: 'create',
        title: 'Create a job',
        paragraphs: [
          'Set `startTime` and `endTime` to the range you need. Use RFC 3339 timestamps with explicit UTC offsets, and put the start no later than the end. Choose a `productId` available to your account, such as `audience`. If you leave out `projectId`, the service uses your default Site Group when one is configured.',
          'Leave out `deviceIds` to include every device in the selected scope. Set `cms` only if the product supports a content management system (CMS) data source. When you send both `loadTime` and `loadTimeEnd`, that ingestion-time range takes precedence over the event-time range.'
        ],
        tables: [
          {
            caption: 'Job creation body',
            headers: ['Field', 'Required', 'Use'],
            rows: [
              ['`startTime`, `endTime`', 'Yes', 'Event-time range with UTC offsets'],
              ['`productId`', 'Yes', 'Data product available to the account'],
              ['`projectId`', 'No', 'Site Group ID; otherwise use the account default'],
              ['`deviceIds`', 'No', 'Device IDs to include'],
              ['`timezone`', 'No', 'IANA timezone for result timestamps'],
              ['`speedUnit`', 'No', 'km or miles for traffic speed conversion'],
              ['`cms`', 'No', 'CMS source when supported by the product'],
              ['`loadTime`, `loadTimeEnd`', 'No', 'Ingestion-time range'],
              ['`summarizedFields`', 'No', 'Aggregation groups']
            ]
          }
        ],
        code: 'curl -X POST \\\n  -H "Authorization: Bearer $ADMOBILIZE_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"startTime":"2026-09-01T00:00:00Z","endTime":"2026-09-02T00:00:00Z","projectId":"PROJECT_ID","productId":"audience"}\' \\\n  "https://api.admobilize.com/v1alpha1/jobs"'
      },
      {
        id: 'create-response',
        title: 'Create response',
        paragraphs: [
          'Creating a job returns a job resource while processing continues in the background. Save its `jobId` for later requests. `status` shows the current state, and `selfLink` gives you the URL for status checks.'
        ],
        code: '{\n  "jobId": "JOB_ID",\n  "status": "RUNNING",\n  "selfLink": "JOB_STATUS_URL"\n}'
      },
      {
        id: 'aggregation',
        title: 'Aggregations',
        paragraphs: [
          'For grouped values instead of raw records, add `summarizedFields` to the create request. Each entry names an aggregation and its fields. Run numeric and categorical aggregations in separate jobs.'
        ],
        tables: [
          {
            caption: 'Aggregation rules',
            headers: ['Field type', 'Supported operation'],
            rows: [
              ['Numeric fields', '`AVG` or `SUM`'],
              ['Categorical fields', '`COUNT`, grouped by the selected field'],
              ['`impressions`', 'Count of rows'],
              ['`views`', 'Uses the stored `isView` field']
            ]
          }
        ],
        code: '{"summarizedFields":[{"aggregation":"COUNT","fields":["impressions"]}]}'
      },
      {
        id: 'status',
        title: 'Job status',
        paragraphs: [
          'Check progress with `GET /jobs/{jid}` no more than once every five minutes. A `status` of `PENDING` or `RUNNING` means the job is still processing. You can read its results when it reaches `DONE`.',
          'The API contract defines `ERROR`, but processing failures may also appear as HTTP errors. `detail` and `statusMessage` can be empty. If a request fails, stop polling and inspect the response before submitting another job.'
        ],
        tables: [
          {
            caption: 'Job states',
            headers: ['Status', 'Meaning'],
            rows: [
              ['`PENDING`', 'Accepted; processing has not started'],
              ['`RUNNING`', 'Processing'],
              ['`DONE`', 'Results are ready'],
              ['`ERROR`', 'Defined by the contract; current failures may surface as HTTP errors']
            ]
          }
        ],
        code: 'curl -H "Authorization: Bearer $ADMOBILIZE_API_KEY" \\\n  "https://api.admobilize.com/v1alpha1/jobs/JOB_ID"'
      },
      {
        id: 'results',
        title: 'Results',
        paragraphs: [
          'Call `GET /jobs/{jid}/results` with the shared pagination parameters. The response includes `jobComplete`, `datapoints`, and `nextPageToken`. `datapoints` is a JSON-encoded string inside the JSON response: parse the response, then parse `datapoints` to get an array of records.',
          'The fields in each record vary with the product, device configuration, and CMS integration.'
        ],
        paragraphLinks: [
          { label: 'shared pagination parameters', slug: 'public-api-getting-started', section: 'pagination' }
        ],
        tables: [
          {
            caption: 'Result response fields',
            headers: ['Field', 'Meaning'],
            rows: [
              ['`jobId`', 'Job that produced this page'],
              ['`jobComplete`', 'Whether processing has completed'],
              ['`datapoints`', 'JSON-encoded string containing the result records'],
              ['`nextPageToken`', 'Token for the next page; empty after the last page']
            ]
          }
        ],
        code: 'curl -H "Authorization: Bearer $ADMOBILIZE_API_KEY" \\\n  "https://api.admobilize.com/v1alpha1/jobs/JOB_ID/results?pageSize=100"'
      },
      {
        id: 'result-response',
        title: 'Example result',
        paragraphs: ['In this example, parsing `datapoints` gives you one record with a `deviceId`.'],
        code: '{\n  "jobId": "JOB_ID",\n  "jobComplete": true,\n  "datapoints": "[{\\"deviceId\\":\\"DEVICE_ID\\"}]",\n  "nextPageToken": ""\n}'
      },
      {
        id: 'result-fields',
        title: 'Result fields',
        paragraphs: [
          'These are examples of fields you may get for each product. Device settings and CMS integrations affect which optional fields appear. Treat a missing field as unavailable.'
        ],
        tables: [
          {
            caption: 'Common field groups by product',
            headers: ['Product', 'Examples'],
            rows: [
              [
                'Audience',
                '`deviceId`, `cameraId`, `detectionId`, `zoneId`, `gender`, `age`, `dwellTime`, `sessionTime`, `timestamp`'
              ],
              ['Traffic', '`deviceId`, `zoneId`, `timestamp`, `count`, `direction`, and optional `event`, `speed`, or `vehicleType`']
            ]
          }
        ]
      },
      {
        id: 'export',
        title: 'Export results',
        paragraphs: [
          'Once a job reaches `DONE`, call `GET /jobs/{jid}/export` for download links. The response includes `jobComplete` and a `urls` array. Set `format` to `json`, `csv`, or `avro`; the default is `json`.'
        ],
        tables: [
          {
            caption: 'Export response fields',
            headers: ['Field', 'Meaning'],
            rows: [
              ['`jobId`', 'Exported job'],
              ['`jobComplete`', 'Whether the job has completed'],
              ['`urls`', 'Download URLs for the exported files']
            ]
          }
        ],
        code: 'curl -H "Authorization: Bearer $ADMOBILIZE_API_KEY" \\\n  "https://api.admobilize.com/v1alpha1/jobs/JOB_ID/export?format=csv"'
      },
      {
        id: 'export-response',
        title: 'Export URLs',
        paragraphs: ['An export can produce several URLs. Download each file before its URL expires.'],
        code: '{\n  "jobId": "JOB_ID",\n  "jobComplete": true,\n  "urls": ["DOWNLOAD_URL"]\n}'
      },
      {
        id: 'dashboard-comparison',
        title: 'Dashboard totals',
        paragraphs: [
          'An unaggregated job returns raw detections. Dashboard charts first apply Site Group preferences and quality filters, then aggregate the data. That can produce different totals.',
          'To match Dashboard totals, check the maximum session time, maximum attention time, and view threshold in Profile and Site Groups. Apply those settings in your calculation.'
        ]
      },
      {
        id: 'data-errors',
        title: 'Job errors',
        paragraphs: [
          'If a status, results, or export request fails, inspect its HTTP response. A processing error may appear there even if the job resource never showed `ERROR`.'
        ],
        tables: [
          {
            caption: 'Data-job failures',
            headers: ['Response', 'Check'],
            rows: [
              ['Dataset or table not found', '`projectId`, `productId`, CMS source, and device scope'],
              ['Invalid product or CMS', 'Choose a supported product and CMS combination'],
              ['Processing failure', 'Inspect the returned error before submitting another job']
            ]
          }
        ]
      }
    ],
    dashboardLinks: [{ label: 'Open Reports', to: '/reports' }],
    related: ['public-api-getting-started', 'public-api-projects'],
    legacy: false
  }
];
